'use client'

import type { Primitive } from '@radix-ui/react-primitive'
import type { TagProps } from './tag'

import React, { useEffect } from 'react'
import { useControllableState } from '@radix-ui/react-use-controllable-state'
import { Check, ChevronsUpDown, LoaderCircleIcon } from 'lucide-react'
import { Popover as PopoverPrimitive } from 'radix-ui'
import { createPortal } from 'react-dom'

import { cn } from '@/shared/lib/utils'

import { Checkbox } from './checkbox-native'
import {
	Command,
	CommandEmpty,
	CommandGroup,
	CommandInput,
	CommandItem,
	CommandList,
	CommandSeparator,
} from './command'
import { Tag, TagRemove } from './tag'
import { Tooltip, TooltipContent, TooltipTrigger } from './tooltip'

export type MultiSelectOptionItem = {
	value: string
	label?: React.ReactNode
}

type MultiSelectContextValue = {
	value: string[]

	open: boolean

	onSelect: (value: string, item: MultiSelectOptionItem) => void

	onDeselect: (value: string, item: MultiSelectOptionItem) => void

	onSelectAll: (value: string[]) => void

	onDeselectAll: () => void

	onSearch?: (keyword?: string | undefined) => void

	filter?: boolean | ((keyword: string, current: string) => boolean)

	disabled?: boolean

	maxCount?: number

	itemCache: Map<string, MultiSelectOptionItem>

	isLoading?: boolean
}

const MultiSelectContext = React.createContext<
	MultiSelectContextValue | undefined
>(undefined)

export function useMultiSelect() {
	const context = React.use(MultiSelectContext)

	if (!context)
		throw new Error('useMultiSelect must be used within MultiSelectProvider')

	return context
}

type MultiSelectProps = React.ComponentPropsWithoutRef<
	typeof PopoverPrimitive.Root
> & {
	value?: string[]
	onValueChange?: (value: string[], items: MultiSelectOptionItem[]) => void
	onSelect?: (value: string, item: MultiSelectOptionItem) => void
	onDeselect?: (value: string, item: MultiSelectOptionItem) => void
	defaultValue?: string[]
	onSearch?: (keyword?: string | undefined) => void
	filter?: boolean | ((keyword: string, current: string) => boolean)
	disabled?: boolean
	maxCount?: number
	isLoading?: boolean
	options?: MultiSelectOptionItem[]
}

const MultiSelect: React.FC<MultiSelectProps> = ({
	value: valueProp,
	onValueChange: onValueChangeProp,
	onDeselect: onDeselectProp,
	onSelect: onSelectProp,
	defaultValue,
	open: openProp,
	onOpenChange,
	defaultOpen,
	onSearch,
	filter,
	disabled,
	maxCount,
	isLoading,
	options,
	...popoverProps
}) => {
	const itemCache = React.useRef(
		new Map<string, MultiSelectOptionItem>()
	).current

	useEffect(() => {
		if (options && options?.length) {
			options.forEach((option) => {
				itemCache.set(option.value, option)
			})
		}
	}, [options, itemCache])
	const handleValueChange = React.useCallback(
		(state: string[]) => {
			if (onValueChangeProp) {
				const items = state.map((value) => itemCache.get(value)!)

				onValueChangeProp(state, items)
			}
		},
		[onValueChangeProp, itemCache]
	)

	const [value, setValue] = useControllableState({
		prop: valueProp,
		defaultProp: defaultValue || [],
		onChange: handleValueChange,
	})

	const [open, setOpen] = useControllableState({
		prop: openProp,
		defaultProp: defaultOpen || false,
		onChange: onOpenChange,
	})

	const handleSelect = React.useCallback(
		(value: string, item: MultiSelectOptionItem) => {
			setValue((prev) => {
				if (prev?.includes(value)) return prev

				onSelectProp?.(value, item)

				return prev ? [...prev, value] : [value]
			})
		},
		[onSelectProp, setValue]
	)
	const handleSelectAll = React.useCallback(
		(values: string[]) => {
			setValue(values)
		},
		[setValue]
	)

	const handleDeselect = React.useCallback(
		(value: string, item: MultiSelectOptionItem) => {
			setValue((prev) => {
				if (!prev || !prev.includes(value)) return prev

				onDeselectProp?.(value, item)

				return prev.filter((v) => v !== value)
			})
		},
		[onDeselectProp, setValue]
	)
	const handleDeselectAll = React.useCallback(() => {
		setValue([])
	}, [setValue])

	const contextValue = React.useMemo(
		() => ({
			value: value || [],
			open: open || false,
			onSearch,
			filter,
			disabled,
			maxCount,
			onSelect: handleSelect,
			onSelectAll: handleSelectAll,
			onDeselect: handleDeselect,
			onDeselectAll: handleDeselectAll,
			itemCache,
			isLoading,
		}),
		[
			value,
			open,
			onSearch,
			filter,
			disabled,
			maxCount,
			handleSelect,
			handleDeselect,
			itemCache,
			isLoading,
		]
	)

	return (
		<MultiSelectContext value={contextValue}>
			<PopoverPrimitive.Root
				{...popoverProps}
				open={open}
				onOpenChange={setOpen}
			/>
		</MultiSelectContext>
	)
}

MultiSelect.displayName = 'MultiSelect'

function PreventClick(e: React.MouseEvent | React.TouchEvent) {
	e.preventDefault()
	e.stopPropagation()
}

const MultiSelectTrigger = ({
	className,
	children,
	ref,
	...props
}: React.ComponentProps<typeof Primitive.button>) => {
	const { disabled, isLoading } = useMultiSelect()

	return (
		<PopoverPrimitive.Trigger ref={ref} asChild>
			<button
				type='button'
				aria-disabled={disabled || isLoading}
				data-disabled={disabled || isLoading}
				{...props}
				className={cn(
					'flex size-full min-h-8 items-center justify-between rounded-lg border border-input bg-input/20 px-3 text-sm whitespace-nowrap ring-offset-background focus:border-ring focus:ring-1 focus:ring-[3px] focus:ring-ring/30 focus:outline-none dark:bg-input/30 [&>span]:line-clamp-1',
					'aria-invalid:border-destructive aria-invalid:ring-[3px] aria-invalid:ring-destructive/20',
					'data-[state=open]:border-primary',
					disabled || isLoading
						? 'cursor-not-allowed opacity-50'
						: 'cursor-text',
					className
				)}
				onClick={disabled || isLoading ? PreventClick : props.onClick}
				onTouchStart={disabled || isLoading ? PreventClick : props.onTouchStart}
			>
				{children}
				{isLoading ? (
					<LoaderCircleIcon className='size-4 shrink-0 animate-spin opacity-50' />
				) : (
					<ChevronsUpDown aria-hidden className='size-4 shrink-0 opacity-50' />
				)}
			</button>
		</PopoverPrimitive.Trigger>
	)
}

MultiSelectTrigger.displayName = 'MultiSelectTrigger'

type MultiSelectValueProps = {
	placeholder?: string
	maxDisplay?: number
	maxItemLength?: number
	getOptionVariant?: (item?: MultiSelectOptionItem) => TagProps['variant']
} & React.ComponentProps<typeof Primitive.div>

const MultiSelectValue = ({
	className,
	placeholder,
	maxDisplay,
	maxItemLength,
	getOptionVariant,
	...props
}: MultiSelectValueProps) => {
	const { value, itemCache, disabled, onDeselect } = useMultiSelect()
	const [firstRendered, setFirstRendered] = React.useState(false)

	const renderRemain =
		maxDisplay && value.length > maxDisplay ? value.length - maxDisplay : 0
	const renderItems = renderRemain ? value.slice(0, maxDisplay) : value
	React.useLayoutEffect(() => {
		setFirstRendered(true)
	}, [])

	if (!value.length || !firstRendered) {
		return (
			<span className='pointer-events-none text-placeholder'>
				{placeholder}
			</span>
		)
	}

	return (
		<div
			className={cn(
				'flex flex-1 flex-nowrap items-center gap-1.5 overflow-x-hidden',
				className
			)}
			{...props}
		>
			{renderItems.map((value) => {
				const item = itemCache.get(value)

				const content = item?.label || value

				const child =
					maxItemLength &&
					typeof content === 'string' &&
					content.length > maxItemLength
						? `${content.slice(0, maxItemLength)}...`
						: content

				const el = (
					<Tag
						key={value}
						className='group/multi-select-badge shrink-1 cursor-pointer rounded-full pr-1.5'
						variant={getOptionVariant?.(item)}
					>
						<span className='truncate'>{child}</span>
						<TagRemove
							disabled={disabled}
							onClick={(e) => {
								e.preventDefault()
								e.stopPropagation()
								onDeselect(value, item!)
							}}
						/>
					</Tag>
				)

				if (child !== content) {
					return (
						<Tooltip key={value}>
							<TooltipTrigger asChild>{el}</TooltipTrigger>
							<TooltipContent>{content}</TooltipContent>
						</Tooltip>
					)
				}

				return el
			})}
			{renderRemain ? (
				<Tooltip>
					<TooltipTrigger asChild>
						<span className='py-.5 cursor-default text-xs leading-4 text-muted-foreground'>
							+{renderRemain}
						</span>
					</TooltipTrigger>
					<TooltipContent className='max-h-[50vh] overflow-auto'>
						{
							<div className='flex flex-col gap-0.5'>
								{value.slice(maxDisplay).map((v) => {
									const item = itemCache.get(v)

									return (
										<span key={v} className='truncate'>
											{item?.label || v}
										</span>
									)
								})}
							</div>
						}
					</TooltipContent>
				</Tooltip>
			) : null}
		</div>
	)
}

MultiSelectValue.displayName = 'MultiSelectValue'

const MultiSelectSearch = (
	props: React.ComponentProps<typeof CommandInput>
) => {
	const { onSearch } = useMultiSelect()

	return <CommandInput onValueChange={onSearch} {...props} />
}
MultiSelectSearch.displayName = 'MultiSelectSearch'

const MultiSelectList = ({
	className,
	...props
}: React.ComponentProps<typeof CommandList>) => (
	<CommandList className={cn('max-h-[unset] p-0', className)} {...props} />
)
MultiSelectList.displayName = 'MultiSelectList'

const MultiSelectContent = ({
	className,
	children,
	...props
}: React.ComponentProps<typeof PopoverPrimitive.Content>) => {
	const context = useMultiSelect()

	const fragmentRef = React.useRef<DocumentFragment>(undefined)

	if (!fragmentRef.current && typeof window !== 'undefined')
		fragmentRef.current = document.createDocumentFragment()

	if (!context.open) {
		return fragmentRef.current
			? createPortal(<Command>{children}</Command>, fragmentRef.current)
			: null
	}

	return (
		<PopoverPrimitive.Portal forceMount>
			<PopoverPrimitive.Content
				align='start'
				sideOffset={4}
				collisionPadding={10}
				className={cn(
					'z-50 w-full rounded-xl border bg-popover p-0 text-popover-foreground shadow-md outline-none data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95'
				)}
				style={
					{
						'--radix-select-content-transform-origin':
							'var(--radix-popper-transform-origin)',
						'--radix-select-content-available-width':
							'var(--radix-popper-available-width)',
						'--radix-select-content-available-height':
							'var(--radix-popper-available-height)',
						'--radix-select-trigger-width': 'var(--radix-popper-anchor-width)',
						'--radix-select-trigger-height':
							'var(--radix-popper-anchor-height)',
					} as React.CSSProperties
				}
				{...props}
			>
				<Command
					className={cn(
						'max-h-96 w-full min-w-[var(--radix-select-trigger-width)] p-0',
						className
					)}
					shouldFilter={!context.onSearch}
				>
					{children}
				</Command>
			</PopoverPrimitive.Content>
		</PopoverPrimitive.Portal>
	)
}

type MultiSelectItemProps = React.ComponentProps<typeof CommandItem> &
	Partial<MultiSelectOptionItem> & {
		onSelect?: (value: string, item: MultiSelectOptionItem) => void
		onDeselect?: (value: string, item: MultiSelectOptionItem) => void
	}

const MultiSelectItem = ({
	value,
	onSelect: onSelectProp,
	onDeselect: onDeselectProp,
	children,
	label,
	disabled: disabledProp,
	className,
	...props
}: MultiSelectItemProps) => {
	const {
		value: contextValue,
		maxCount,
		onSelect,
		onDeselect,
		itemCache,
	} = useMultiSelect()

	const item = React.useMemo(
		() =>
			value
				? {
						value,
						label:
							label || (typeof children === 'string' ? children : undefined),
					}
				: undefined,
		[value, label, children]
	)

	const selected = Boolean(value && contextValue.includes(value))

	React.useEffect(() => {
		if (value) itemCache.set(value, item!)
	}, [selected, value, item, itemCache])

	const disabled = Boolean(
		disabledProp || (!selected && maxCount && contextValue.length >= maxCount)
	)

	const handleClick = () => {
		if (selected) {
			onDeselectProp?.(value!, item!)
			onDeselect(value!, item!)
		} else {
			itemCache.set(value!, item!)
			onSelectProp?.(value!, item!)
			onSelect(value!, item!)
		}
	}

	return (
		<CommandItem
			{...props}
			value={value}
			className={cn(
				disabled && 'cursor-not-allowed text-muted-foreground',
				className
			)}
			disabled={disabled}
			onSelect={!disabled && value ? handleClick : undefined}
		>
			<span className='mr-2 truncate'>{children || label || value}</span>
			{selected ? <Check className='ml-auto size-4 shrink-0' /> : null}
		</CommandItem>
	)
}
MultiSelectItem.displayName = 'MultiSelectItem'

const MultiSelectCheckboxItem = ({
	value,
	onSelect: onSelectProp,
	onDeselect: onDeselectProp,
	children,
	label,
	disabled: disabledProp,
	className,
	...props
}: MultiSelectItemProps) => {
	const {
		value: contextValue,
		maxCount,
		onSelect,
		onDeselect,
		itemCache,
	} = useMultiSelect()

	const item = React.useMemo(
		() =>
			value
				? {
						value,
						label:
							label || (typeof children === 'string' ? children : undefined),
					}
				: undefined,
		[value, label, children]
	)

	const selected = Boolean(value && contextValue.includes(value))

	React.useEffect(() => {
		if (value) itemCache.set(value, item!)
	}, [selected, value, item, itemCache])

	const disabled = Boolean(
		disabledProp || (!selected && maxCount && contextValue.length >= maxCount)
	)

	const handleClick = () => {
		if (selected) {
			onDeselectProp?.(value!, item!)
			onDeselect(value!, item!)
		} else {
			itemCache.set(value!, item!)
			onSelectProp?.(value!, item!)
			onSelect(value!, item!)
		}
	}

	return (
		<CommandItem
			{...props}
			value={value}
			className={cn(
				disabled && 'cursor-not-allowed text-muted-foreground',
				className
			)}
			disabled={disabled}
			onSelect={!disabled && value ? handleClick : undefined}
		>
			<Checkbox checked={selected} onChange={() => {}} />
			{children || label || value}
		</CommandItem>
	)
}
MultiSelectCheckboxItem.displayName = 'MultiSelectCheckboxItem'

const MultiSelectGroup = (props: React.ComponentProps<typeof CommandGroup>) => (
	<CommandGroup {...props} />
)
MultiSelectGroup.displayName = 'MultiSelectGroup'

const MultiSelectSeparator = (
	props: React.ComponentProps<typeof CommandSeparator>
) => <CommandSeparator {...props} />
MultiSelectSeparator.displayName = 'MultiSelectSeparator'

const MultiSelectEmpty = ({
	children = 'No Content',
	...props
}: React.ComponentProps<typeof CommandEmpty>) => (
	<CommandEmpty {...props}>{children}</CommandEmpty>
)

MultiSelectEmpty.displayName = 'MultiSelectEmpty'

const MultiSelectCheckboxSelectAll = ({
	values,
	children,
	className,
	...props
}: {
	values: string[]
	children: React.ReactNode
	className?: string
}) => {
	const { value: contextValue, onDeselectAll, onSelectAll } = useMultiSelect()
	const selected = values?.every((v) => contextValue.indexOf(v) !== -1)
	const indeterminate =
		!selected && values?.some((option) => contextValue.indexOf(option) !== -1)

	const handleClick = () => {
		if (selected) {
			onDeselectAll()
		} else {
			onSelectAll(values)
		}
	}

	return (
		<CommandItem {...props} className={className} onSelect={handleClick}>
			<Checkbox
				indeterminate={indeterminate}
				checked={selected}
				onChange={() => {}}
			/>
			<span className='mr-2 truncate'>{children}</span>
		</CommandItem>
	)
}

export type MultiSelectOptionSeparator = {
	type: 'separator'
}

export type MultiSelectOptionGroup = {
	heading?: React.ReactNode
	value?: string
	children: MultiSelectOption[]
}

export type MultiSelectOption =
	| Pick<
			MultiSelectItemProps,
			'value' | 'label' | 'disabled' | 'onSelect' | 'onDeselect'
	  >
	| MultiSelectOptionSeparator
	| MultiSelectOptionGroup

function renderMultiSelectOptions(list: MultiSelectOption[]) {
	return list.map((option, index) => {
		if ('type' in option) {
			if (option.type === 'separator')
				return <MultiSelectSeparator key={index} />

			return null
		}

		if ('children' in option) {
			return (
				<MultiSelectGroup
					key={option.value || index}
					value={option.value}
					heading={option.heading}
				>
					{renderMultiSelectOptions(option.children)}
				</MultiSelectGroup>
			)
		}

		return (
			<MultiSelectItem key={option.value} {...option}>
				{option.label}
			</MultiSelectItem>
		)
	})
}

export {
	MultiSelect,
	MultiSelectTrigger,
	MultiSelectValue,
	MultiSelectSearch,
	MultiSelectContent,
	MultiSelectList,
	MultiSelectItem,
	MultiSelectCheckboxItem,
	MultiSelectGroup,
	MultiSelectSeparator,
	MultiSelectEmpty,
	MultiSelectCheckboxSelectAll,
	renderMultiSelectOptions,
}
